import React, { useEffect, useState } from 'react'
import * as ZonaEntregaHttp from '../../../../services/api/https/registration/zona-entrega.http'
import * as SystemHttp from '../../../../services/api/https/system/system.http'
import { connect } from 'react-redux'
import { errorHandler } from '../../../../services/util/error.service'
import {
  hideLoadingBox,
  showLoadingBox,
  showMessageBox,
} from '../../../../store/actions/system.actions'
import {
  Btn,
  BtnIcon,
  CustomCheckbox,
  CustomDataTableHeader,
  CustomDropdown,
  CustomModal,
  InputText,
} from '../../../../components'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { FaRegTrashAlt } from 'react-icons/fa'
import { tableCellCentered } from '../../../../assets/js/styles'

const LOAD_MESSAGE = {
  NONE: 'NONE',
  INIT: 'Adquirindo informações iniciais',
  SAVE: 'Salvando zona de entrega',
  SUCCESS: 'Zona de Entrega salva com sucesso',
}

/**
 * Precisa ser uma função: se fosse um objeto único, os objetos/arrays aninhados
 * (zonaEntrega, selectedCities) seriam compartilhados entre todas as aberturas do
 * modal e os dados da zona anterior vazariam para a próxima.
 */
const buildInitialState = () => ({
  zonaEntrega: {
    id: undefined,
    name: '',
    actived: true,
    tp_id: null,
    cities: [],
  },
  selectedCities: [],
  selectedUf: null,
  ufs: [],
  cities: [],
  savedZonaEntrega: {},
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
  globalFilter: '',
})

const ZonaEntregaModal = ({ show, onModalClose, onModalSave, data, selectedTp, dispatch }) => {
  const [state, setState] = useState(buildInitialState)

  const saveZonaEntrega = async ({ created_at, updated_at, ...zonaEntrega }) => {
    if (!zonaEntrega.name)
      return setState(prevState => ({
        ...prevState,
        errorMessage: 'Nome é obrigatório',
        loadingMessage: LOAD_MESSAGE.NONE,
      }))

    try {
      let res = null
      if (zonaEntrega.id)
        res = await ZonaEntregaHttp.updateZonaEntrega({
          ...zonaEntrega,
          cities: state.selectedCities,
        })
      else
        res = await ZonaEntregaHttp.saveZonaEntrega({
          ...zonaEntrega,
          cities: state.selectedCities,
        })
      setState(prevState => ({
        ...prevState,
        savedZonaEntrega: res,
        loadingMessage: LOAD_MESSAGE.SUCCESS,
      }))
    } catch (err) {
      setState(prevState => ({
        ...prevState,
        errorMessage: errorHandler(err),
        loadingMessage: LOAD_MESSAGE.NONE,
      }))
    }
  }

  // Events
  const onBtnSalvarClicked = async () => {
    setState(prevState => ({ ...prevState, loadingMessage: LOAD_MESSAGE.SAVE }))
  }

  const onFormContentChange = (name, value) => {
    setState(prevState => ({
      ...prevState,
      zonaEntrega: { ...prevState.zonaEntrega, [name]: value },
    }))
  }

  const fetchCities = async ufId => {
    await SystemHttp.getCities(ufId)
      .then(cities => {
        setState(prevState => ({
          ...prevState,
          cities: cities,
        }))
      })
      .catch(err => setState(prevState => ({ ...prevState, errorMessage: errorHandler(err) })))
  }

  // Helpers
  const fetchInitInfo = async zonaEntrega => {
    setState({
      ...buildInitialState(),
      zonaEntrega: zonaEntrega ? { ...zonaEntrega } : buildInitialState().zonaEntrega,
      selectedCities: zonaEntrega ? [...(zonaEntrega.cities || [])] : [],
      loadingMessage: LOAD_MESSAGE.INIT,
    })

    try {
      const ufs = await SystemHttp.getUfs()
      setState(prevState => ({ ...prevState, ufs }))
    } catch (err) {
      setState(prevState => ({ ...prevState, errorMessage: errorHandler(err) }))
    }
  }

  useEffect(() => {
    if (state.selectedUf) fetchCities(state.selectedUf.id)
  }, [state.selectedUf])

  const resetModal = () => {
    setState(buildInitialState())
  }

  //Hooks
  useEffect(() => {
    if (show) fetchInitInfo(data)
  }, [show])

  useEffect(() => {
    switch (state.loadingMessage) {
      case LOAD_MESSAGE.NONE:
        dispatch(hideLoadingBox())
        break

      case LOAD_MESSAGE.SAVE:
        dispatch(showLoadingBox('Aguarde...', state.loadingMessage))
        const formattedZonaEntrega = {
          ...state.zonaEntrega,
          tp_id: selectedTp,
        }
        saveZonaEntrega(formattedZonaEntrega)
        break

      case LOAD_MESSAGE.SUCCESS:
        dispatch(hideLoadingBox())
        dispatch(showMessageBox(state.loadingMessage, null))
        onModalSave(state.savedZonaEntrega)
        break

      default:
        break
    }
  }, [state.loadingMessage])

  useEffect(() => {
    if (state.errorMessage) {
      dispatch(showMessageBox(null, state.errorMessage))
      setState(prevState => ({
        ...prevState,
        loadingMessage: LOAD_MESSAGE.NONE,
        errorMessage: null,
      }))
    }
  }, [state.errorMessage])

  const handleRemoveCity = (event, city) => {
    event.preventDefault()
    setState(prevState => ({
      ...prevState,
      selectedCities: prevState.selectedCities.filter(c => c.id !== city.id),
    }))
  }

  const handleAddAllCities = () => {
    if (!state.selectedUf || !state.cities.length) return

    setState(prevState => {
      const newCities = state.cities.map(city => ({
        ...city,
        uf: state.selectedUf.uf,
      }))

      const citiesToAdd = newCities.filter(
        newCity => !prevState.selectedCities.some(selectedCity => selectedCity.id === newCity.id),
      )

      return { ...prevState, selectedCities: [...prevState.selectedCities, ...citiesToAdd] }
    })
  }

  const handleRemoveAllCitiesFromState = () => {
    if (!state.selectedUf) return

    setState(prevState => ({
      ...prevState,
      selectedCities: prevState.selectedCities.filter(city => city.uf !== state.selectedUf.uf),
    }))
  }

  const onGlobalFilterChange = value => {
    setState(prevState => ({ ...prevState, globalFilter: value }))
  }

  return (
    <CustomModal
      header="Zona de Entrega"
      isVisible={show}
      onClose={() => {
        resetModal()
        onModalClose()
      }}
      width={800}
    >
      <form className="p-grid">
        <div className="p-col-12">
          <InputText
            label="Nome:*"
            value={state.zonaEntrega.name}
            onChange={value => onFormContentChange('name', value.toUpperCase())}
          />
        </div>

        <div className="p-col-6">
          <CustomDropdown
            optionLabel="name"
            options={state.ufs}
            value={state.selectedUf}
            label="Estado:"
            filter={true}
            onChange={value => {
              setState(prevState => ({ ...prevState, selectedUf: value }))
            }}
          />
        </div>
        <div className="p-col-6">
          <CustomDropdown
            disabled={!state.selectedUf}
            optionLabel="name"
            options={state.cities}
            label="Cidade:"
            filter={true}
            onChange={value => {
              state.selectedCities.find(city => city.id === value.id) ||
                setState(prevState => ({
                  ...prevState,
                  selectedCities: [
                    { ...value, uf: state.selectedUf.uf },
                    ...prevState.selectedCities,
                  ],
                }))
            }}
          />
        </div>
        {state.selectedUf && (
          <div className="p-col-12">
            <Btn
              disabled={!state.selectedUf || !state.cities.length}
              onClick={handleAddAllCities}
              style={{ marginBottom: '10px', marginRight: '10px' }}
            >
              Adicionar Todas as cidades de {state.selectedUf.uf}
            </Btn>
            <Btn
              disabled={
                !state.selectedUf ||
                !state.selectedCities.some(city => city.uf === state.selectedUf.uf)
              }
              onClick={handleRemoveAllCitiesFromState}
              style={{ marginBottom: '10px', backgroundColor: '#dc3545', borderColor: '#dc3545' }}
            >
              Remover Todas as cidades de {state.selectedUf.uf}
            </Btn>
          </div>
        )}
        <div className="p-col-12">
          <CustomCheckbox
            checked={state.zonaEntrega.actived}
            onChange={value => onFormContentChange('actived', value)}
          >
            Ativo
          </CustomCheckbox>
        </div>
        <div className="p-col-12">
          <DataTable
            value={state.selectedCities}
            paginator={true}
            rows={5}
            globalFilter={state.globalFilter}
            header={<CustomDataTableHeader onChange={value => onGlobalFilterChange(value)} />}
          >
            <Column field="name" header="Cidade" />
            <Column field="uf" header="UF" style={tableCellCentered(80)} />
            <Column
              header=""
              style={tableCellCentered(80)}
              body={city => (
                <BtnIcon onClick={e => handleRemoveCity(e, city)}>
                  <FaRegTrashAlt color="red" size={18} />
                </BtnIcon>
              )}
            />
          </DataTable>
        </div>
        <div className="p-col-12">
          <Btn onClick={onBtnSalvarClicked}>Salvar</Btn>
        </div>
      </form>
    </CustomModal>
  )
}

const mapStateToProps = ({ system: { selectedTp } }) => ({ selectedTp })
export default connect(mapStateToProps)(ZonaEntregaModal)
